import { CategoryFieldName } from '@infotech-soe/metaschema';
import {
  ChangeEvent,
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MultiGrid } from 'react-virtualized';

import { DatasetGridColumn } from '../../../../services/dataset/view-model/DatasetGridColumn';
import { DatasetGridRow } from '../../../../services/dataset/view-model/DatasetGridRow';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  getDatasetSearchCaseSensitive,
  getDatasetSearchContains,
} from '../../../../store/selectors/userConfig';
import userConfigActions from '../../../../store/slices/userConfig';
import { SEARCH_DIRECTIONS } from '../constants';
import { IntlFormatter } from '../utils/getFormattedValue';

type SearchHandler = (
  event: KeyboardEvent | ChangeEvent<HTMLInputElement>,
  searchRows?: DatasetGridRow[],
  direction?: SEARCH_DIRECTIONS
) => void;

interface UseDatasetSearchProps {
  rows: DatasetGridRow[];
  orderedColumns: DatasetGridColumn[];
  activeCell: number;
  activeRow: number;
  rowIdField: CategoryFieldName;
  intlFormatter: IntlFormatter;
  gridFocus: () => void;
  gridRef: MutableRefObject<MultiGrid | null>;
  containerRef: MutableRefObject<HTMLElement | null>;
  setActiveRow: Dispatch<SetStateAction<number>>;
  setShouldDisableScroll: Dispatch<SetStateAction<boolean>>;
}

const useDatasetSearch = ({
  rows,
  orderedColumns,
  activeCell,
  activeRow,
  rowIdField,
  intlFormatter,
  gridFocus,
  gridRef,
  containerRef,
  setActiveRow,
  setShouldDisableScroll,
}: UseDatasetSearchProps) => {
  const dispatch = useAppDispatch();
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchInvalid, setSearchInvalid] = useState(false);
  const searchContains = useAppSelector(getDatasetSearchContains);
  const searchCaseSensitive = useAppSelector(getDatasetSearchCaseSensitive);

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchContainsRef = useRef(false);
  const searchCaseSensitiveRef = useRef(false);
  searchContainsRef.current = searchContains;
  searchCaseSensitiveRef.current = searchCaseSensitive;

  const activeColumn = useMemo(
    () => orderedColumns[activeCell],
    [orderedColumns, activeCell]
  );

  const isRowMatched = useCallback(
    (
      row: DatasetGridRow,
      value: string,
      contains: boolean,
      caseSensitive: boolean
    ): boolean => {
      if (!value || !activeColumn) return false;

      const { field, domain, formatter, format } = activeColumn;
      if (row[field] === null) return false;

      if (domain.type === 'number') {
        const strVal = row[field].toString();
        return contains ? strVal.includes(value) : strVal.startsWith(value);
      }

      const displayVal = formatter
        ? formatter(row[field], { row })
        : intlFormatter(format, row[field], domain);

      const haystack = displayVal?.toString().trim() ?? '';
      const needle = caseSensitive ? value : value.toLowerCase();
      const normalizedHaystack = caseSensitive
        ? haystack
        : haystack.toLowerCase();

      return contains
        ? normalizedHaystack.includes(needle)
        : normalizedHaystack.startsWith(needle);
    },
    [activeColumn, intlFormatter]
  );

  const findMatchIndex = useCallback(
    (
      searchRows: DatasetGridRow[],
      value: string,
      contains: boolean,
      caseSensitive: boolean
    ): number =>
      searchRows.findIndex(col =>
        isRowMatched(col, value, contains, caseSensitive)
      ),
    [isRowMatched]
  );

  const hasMatchInRows = useCallback(
    (searchRows: DatasetGridRow[]): boolean =>
      findMatchIndex(
        searchRows,
        searchValue,
        searchContains,
        searchCaseSensitive
      ) >= 0,
    [findMatchIndex, searchValue, searchContains, searchCaseSensitive]
  );

  const matchedRowIds = useMemo(() => {
    if (!searchValue) return [];

    return rows
      .filter(col =>
        isRowMatched(col, searchValue, searchContains, searchCaseSensitive)
      )
      .map(col => col[rowIdField]);
  }, [
    searchValue,
    rows,
    searchContains,
    searchCaseSensitive,
    rowIdField,
    isRowMatched,
  ]);

  const totalSearchMatches = matchedRowIds.length;

  const currentSearchMatch = useMemo(() => {
    if (!totalSearchMatches) return 0;

    const currentRowId = rows[activeRow]?.[rowIdField];
    const currentMatchIndex = matchedRowIds.indexOf(currentRowId);

    return currentMatchIndex >= 0 ? currentMatchIndex + 1 : 0;
  }, [totalSearchMatches, rows, activeRow, rowIdField, matchedRowIds]);

  const handleSearch = useCallback<SearchHandler>(
    (_event, searchRows = rows, direction) => {
      const value = searchInputRef.current?.value || '';

      setSearchValue(value);

      if (!value) {
        setSearchInvalid(false);
        return;
      }

      const columnIndex = findMatchIndex(
        searchRows,
        value,
        searchContainsRef.current,
        searchCaseSensitiveRef.current
      );

      if (columnIndex >= 0) {
        setShouldDisableScroll(false);
        switch (direction) {
          case SEARCH_DIRECTIONS.NEXT:
            setActiveRow(columnIndex + rows.length - searchRows.length);
            break;
          case SEARCH_DIRECTIONS.PREV:
            setActiveRow(
              rows.findIndex(
                col => col[rowIdField] === searchRows[columnIndex][rowIdField]
              )
            );
            break;
          default:
            setActiveRow(columnIndex);
        }
        setSearchInvalid(false);
      } else if (direction === undefined) {
        setSearchInvalid(true);
      }
    },
    [findMatchIndex, rows, rowIdField, setActiveRow, setShouldDisableScroll]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      if (!value) {
        setSearchValue('');
        setSearchInvalid(false);
        return;
      }
      handleSearch({} as ChangeEvent<HTMLInputElement>);
    },
    [handleSearch]
  );

  const handleToggleContains = useCallback(() => {
    const newValue = !searchContainsRef.current;
    searchContainsRef.current = newValue;
    dispatch(
      userConfigActions.saveGeneral({
        config: { datasetSearchContains: newValue },
      })
    );
    if (showSearchInput && searchInputRef.current?.value) {
      handleSearch({} as ChangeEvent<HTMLInputElement>);
    }
  }, [dispatch, showSearchInput, handleSearch]);

  const handleToggleCaseSensitive = useCallback(() => {
    const newValue = !searchCaseSensitiveRef.current;
    searchCaseSensitiveRef.current = newValue;
    dispatch(
      userConfigActions.saveGeneral({
        config: { datasetSearchCaseSensitive: newValue },
      })
    );
    if (showSearchInput && searchInputRef.current?.value) {
      handleSearch({} as ChangeEvent<HTMLInputElement>);
    }
  }, [dispatch, showSearchInput, handleSearch]);

  const hasPrev = useMemo(() => {
    if (!searchValue || searchInvalid) return false;
    return hasMatchInRows(rows.slice(0, activeRow).reverse());
  }, [searchValue, searchInvalid, activeRow, rows, hasMatchInRows]);

  const hasNext = useMemo(() => {
    if (!searchValue || searchInvalid) return false;
    return hasMatchInRows(rows.slice(activeRow + 1));
  }, [searchValue, searchInvalid, activeRow, rows, hasMatchInRows]);

  const handleSearchPrev = useCallback(() => {
    if (!searchInputRef.current?.value) return;
    if (activeRow > 0) {
      handleSearch(
        {} as ChangeEvent<HTMLInputElement>,
        [...rows].slice(0, activeRow).reverse(),
        SEARCH_DIRECTIONS.PREV
      );
    }
  }, [rows, activeRow, handleSearch]);

  const handleSearchNext = useCallback(() => {
    if (!searchInputRef.current?.value) return;
    if (activeRow < rows.length - 1) {
      handleSearch(
        {} as ChangeEvent<HTMLInputElement>,
        [...rows].slice(activeRow + 1),
        SEARCH_DIRECTIONS.NEXT
      );
    }
  }, [rows, activeRow, handleSearch]);

  const closeSearchInput = useCallback(
    (relatedTarget?: EventTarget | null) => {
      setShouldDisableScroll(true);
      setSearchValue('');
      setSearchInvalid(false);
      setShowSearchInput(false);
      if (
        !relatedTarget ||
        containerRef.current?.contains(relatedTarget as Node)
      ) {
        gridFocus();
      }
    },
    [gridFocus, containerRef, setShouldDisableScroll]
  );

  useEffect(() => {
    if (showSearchInput) searchInputRef.current?.focus();
  }, [showSearchInput]);

  useEffect(() => {
    gridRef.current?.forceUpdateGrids();
  }, [searchValue, searchContains, searchCaseSensitive, gridRef]);

  return {
    showSearchInput,
    setShowSearchInput,
    searchValue,
    searchInvalid,
    searchContains,
    searchCaseSensitive,
    searchInputRef,
    handleSearch,
    handleSearchChange,
    handleToggleContains,
    handleToggleCaseSensitive,
    handleSearchPrev,
    handleSearchNext,
    hasPrev,
    hasNext,
    currentSearchMatch,
    totalSearchMatches,
    closeSearchInput,
  };
};

export default useDatasetSearch;
